/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'

import React from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { View } from '@components/atoms'
import { Footer } from '@components/systems'
import { MonolithAccountNav } from '@components/systems/monolith/MonolithAccountNav'
import { MonolithConfirmModal } from '@components/systems/monolith/MonolithConfirmModal'
import { FullModalLayout } from '@components/molecules'
import { mono } from '@components/systems/monolith/monolith.tokens'
import {
	wholesaleMoney,
	wholesaleStatusTitle,
	wholesaleAddress,
	wholesaleDate,
	hasWholesaleShipment,
} from '@components/systems/monolith/wholesale/wholesaleOrder.format'
import { downloadWholesaleOrdersExcel } from '@components/systems/monolith/wholesale/wholesaleOrderExcel'
import { downloadWholesaleOrderDocument } from '@components/systems/monolith/wholesale/wholesaleOrderDocument'
import { MonolithOrderList } from '@components/systems/monolith/order/MonolithOrderList'
import { MonolithOrderDetailModal } from '@components/systems/monolith/order/MonolithOrderDetailModal'
import { customerAccountStyle } from './customerAccount.style'
import { customerOrderListStyle } from './customerOrderList.style'

import { useAppDispatch, useAppSelector, ApiGetDataStatus } from '@redux/index'
import dayjs from 'dayjs'
import { wholesaleActions } from '@redux/store/index'
import {
	OrderType,
	WholesaleCatalogType,
	WholesaleReceiverType,
	emptyWholesaleReceiver,
} from '@schemas'

const money = (currency: string, value: number): string =>
	currency === 'KRW'
		? `₩${Math.round(value).toLocaleString('ko-KR')}`
		: `$${value.toLocaleString('en-US', {
				minimumFractionDigits: 2,
				maximumFractionDigits: 2,
		  })}`

const toNumber = (v: string): number => {
	const n = Number(String(v).replace(/,/g, ''))
	return Number.isNaN(n) || n < 0 ? 0 : n
}

/**
 * Wholesale Orders — 딜러 · 디스트리뷰터 계정 페이지 (DISPATCH_PLAN §5 · PARTNER_PLAN §3).
 * 위 밴드: 납품가로 주문(제품 · 단가 · 수량 · 합계 → 수취인 → Place order).
 * 아래: 내 도매 주문 목록 — 주문번호 · 날짜 · 상태 · 금액 · Detail(2026-10-06). 그 전엔 백오피스 Grid 를 그대로 써서
 *   한국어 툴바 · 컬럼/필터 사이드바가 고객에게 보였다. 주문이 없어도 열 머리는 그린다. Download Excel = 이 목록.
 *   Detail = /customer/wholesale/:orderId — 전체 화면 모달(주문 한 건 전부), 맨 아래 Download PDF(pdf-lib, 한글이 있으면 나눔고딕).
 * 값·재고 판정의 정본은 서버(`POST /wholesale/order`)다 — 여기 합계는 미리보기.
 */
const MonolithCustomerWholesalePage = () => {
	const reduxDispatch = useAppDispatch()

	const catalog: WholesaleCatalogType | null = useAppSelector(
		state => state.wholesale.catalog,
	)
	const catalogStatus: ApiGetDataStatus = useAppSelector(
		state => state.wholesale.catalogStatus,
	)
	const orderList: OrderType[] = useAppSelector(state => state.wholesale.orderList)
	const orderListStatus: ApiGetDataStatus = useAppSelector(
		state => state.wholesale.orderListStatus,
	)
	const isPlacing: boolean = useAppSelector(state => state.wholesale.isPlacing)
	const placeResult = useAppSelector(state => state.wholesale.placeResult)

	const [qty, setQty] = React.useState<{ [productId: string]: string }>({})
	const [receiver, setReceiver] = React.useState<WholesaleReceiverType>(
		emptyWholesaleReceiver(),
	)
	const [shipping, setShipping] = React.useState<string>('')
	const [duty, setDuty] = React.useState<string>('')
	const [memo, setMemo] = React.useState<string>('')
	const [isConfirmOpen, setIsConfirmOpen] = React.useState<boolean>(false)
	/** 목록이 기본. 「New order」를 누르면 주문 화면으로 바뀐다(사용자 결정 2026-09-26) */
	const [view, setView] = React.useState<'list' | 'new'>('list')
	// 자세히 = 주소 /customer/wholesale/:orderId (주문마다 자기 주소, 2026-10-06). 목록에서 그 주문을 찾는다
	const { orderId } = useParams<{ orderId: string }>()
	const navigate = useNavigate()
	const detailOrder: OrderType | null =
		orderId === undefined
			? null
			: orderList.find(anOrder => anOrder.id === orderId) ?? null
	// 목록을 다 받았는데 그 주문이 없으면(남의 주문 · 없는 번호) 목록으로
	React.useEffect(() => {
		if (orderId === undefined) return
		if (orderListStatus !== 'ok' && orderListStatus !== 'empty') return
		if (!orderList.some(anOrder => anOrder.id === orderId)) {
			navigate('/customer/wholesale', { replace: true })
		}
	}, [orderId, orderList, orderListStatus])

	const openDetail = (id: string) =>
		navigate(`/customer/wholesale/${encodeURIComponent(id)}`)
	const closeDetail = () => navigate('/customer/wholesale')

	const didInit = React.useRef<boolean>(false)
	React.useEffect(() => {
		if (didInit.current) return
		didInit.current = true
		reduxDispatch(wholesaleActions.TRIGGER_INIT_WHOLESALE_PAGE(true))
	}, [reduxDispatch])

	// 직전 주문의 수취인을 미리 채운다(CUSTOMER 에 주소가 없다). 이미 적은 게 있으면 덮지 않는다
	React.useEffect(() => {
		if (catalog?.lastReceiver && receiver.street1 === '' && receiver.givenName === '') {
			setReceiver(catalog.lastReceiver)
		}
	}, [catalog])

	// 주문이 되면 수량을 비운다
	React.useEffect(() => {
		if (placeResult?.ok) {
			setQty({})
			setMemo('')
			setView('list')
		}
	}, [placeResult])

	const currency = catalog?.currency ?? 'USD'
	const lines = (catalog?.items ?? [])
		.map(anItem => ({
			...anItem,
			qty: Math.floor(toNumber(qty[anItem.productId] ?? '')),
		}))
		.filter(aLine => aLine.qty > 0)
	const subtotal: number = lines.reduce(
		(sum: number, aLine) => sum + Number(aLine.unitAmount) * Number(aLine.qty),
		0,
	)
	const vat =
		catalog?.vatExtraYn === 1 && currency === 'KRW' ? Math.round(subtotal * 0.1) : 0
	const shippingAmount: number = catalog?.shippingExtraYn === 1 ? toNumber(shipping) : 0
	const dutyAmount: number = catalog?.dutyExtraYn === 1 ? toNumber(duty) : 0
	const total: number = subtotal + vat + shippingAmount + dutyAmount

	const receiverOk =
		receiver.givenName.trim() !== '' &&
		receiver.phoneNumber.trim() !== '' &&
		receiver.street1.trim() !== '' &&
		receiver.countryName.trim() !== '' &&
		receiver.countryIsoCode.trim().length === 2
	const canPlace = lines.length > 0 && receiverOk && !isPlacing
	const canOpenNew =
		catalog !== null && catalog.seasonId !== '' && catalog.items.length > 0
	const newHint =
		catalogStatus === 'loading' || catalogStatus === 'init'
			? ''
			: catalog === null
			? 'Wholesale ordering is not set up for this account.'
			: catalog.seasonId === ''
			? 'No open season.'
			: catalog.items.length === 0
			? 'No products are priced for this account yet.'
			: ''

	const editReceiver = (key: keyof WholesaleReceiverType, value: string) =>
		setReceiver(prev => ({ ...prev, [key]: value }))

	const onPlace = () => {
		setIsConfirmOpen(false)
		reduxDispatch(
			wholesaleActions.PLACE_ORDER({
				items: lines.map(aLine => ({ productId: aLine.productId, qty: aLine.qty })),
				receiver: {
					...receiver,
					countryIsoCode: receiver.countryIsoCode.trim().toUpperCase(),
				},
				shippingAmount,
				dutyAmount,
				memo: memo.trim(),
			}),
		)
	}

	const field = (
		key: keyof WholesaleReceiverType,
		label: string,
		opts: { required?: boolean; autoComplete?: string; maxLength?: number } = {},
	) => (
		<View className="mnField" key={key}>
			<label className="mnLabel" htmlFor={`ws-${key}`}>
				{label}
				{opts.required ? ' *' : ''}
			</label>
			<input
				id={`ws-${key}`}
				type="text"
				className="mnInput"
				value={receiver[key]}
				maxLength={opts.maxLength}
				autoComplete={opts.autoComplete}
				onChange={e => editReceiver(key, e.target.value)}
			/>
		</View>
	)

	return (
		<View css={style}>
			<View className="mnWrap">
				<MonolithAccountNav />

				<View className="mnAccountBody wsBody">
					{/*
						New order — 페이지 위에 덮이는 전체 화면 모달(사용자 결정 2026-09-26). 백오피스 자세히와 같은
						FullModalLayout overlay(body 포털, Esc 닫힘). 포털이라 페이지 스타일이 닿지 않아 안에서 다시 감싼다.
					*/}
					{view === 'new' && catalog !== null && (
						<FullModalLayout
							overlay
							size="large"
							title="New order"
							className="wsNewModal"
							closeLabel="Close"
							onCloseClick={() => setView('list')}
						>
							<View css={style} className="wsNewView">
								<View className="wsOrderBand">
									<View className="wsBandHead">
										<span className="mnKicker">New order</span>
										<span className="mnMeta">
											{catalog.companyName} · {currency}
											{catalog.vatExtraYn === 1 ? ' · VAT added separately' : ''}
											{catalog.shippingExtraYn === 1
												? ' · shipping billed separately'
												: ''}
											{catalog.dutyExtraYn === 1 ? ' · duties billed separately' : ''}
										</span>
									</View>

									<View className="wsTable" role="table">
										<View className="wsRow wsRowHead" role="row">
											<span>Product</span>
											<span className="right">Unit price</span>
											<span className="right">Qty</span>
											<span className="right">Line total</span>
										</View>
										{catalog.items.map(anItem => {
											const q = Math.floor(toNumber(qty[anItem.productId] ?? ''))
											return (
												<View className="wsRow" role="row" key={anItem.productId}>
													<span className="wsProduct">
														<span className="wsProductName">{anItem.name}</span>
														<span className="mnMeta">{anItem.code}</span>
													</span>
													<span className="right num">
														{money(currency, anItem.unitAmount)}
													</span>
													<span className="right">
														<input
															type="number"
															min={0}
															step={1}
															className="mnInput wsQty"
															value={qty[anItem.productId] ?? ''}
															placeholder="0"
															onChange={e =>
																setQty(prev => ({
																	...prev,
																	[anItem.productId]: e.target.value,
																}))
															}
															aria-label={`${anItem.name} quantity`}
														/>
													</span>
													<span className="right num">
														{q > 0 ? money(currency, anItem.unitAmount * q) : '—'}
													</span>
												</View>
											)
										})}
									</View>

									<View className="wsTotals">
										<View className="wsTotalRow">
											<span>Subtotal</span>
											<span className="num">{money(currency, subtotal)}</span>
										</View>
										{catalog.vatExtraYn === 1 && currency === 'KRW' && (
											<View className="wsTotalRow">
												<span>VAT (10%)</span>
												<span className="num">{money(currency, vat)}</span>
											</View>
										)}
										{catalog.shippingExtraYn === 1 && (
											<View className="wsTotalRow">
												<span>Shipping</span>
												<input
													type="text"
													inputMode="decimal"
													className="mnInput wsAmountInput"
													value={shipping}
													placeholder="0"
													onChange={e => setShipping(e.target.value)}
													aria-label="Shipping"
												/>
											</View>
										)}
										{catalog.dutyExtraYn === 1 && (
											<View className="wsTotalRow">
												<span>Duties</span>
												<input
													type="text"
													inputMode="decimal"
													className="mnInput wsAmountInput"
													value={duty}
													placeholder="0"
													onChange={e => setDuty(e.target.value)}
													aria-label="Duties"
												/>
											</View>
										)}
										{catalog.shippingExtraYn === 0 && (
											<View className="wsTotalRow faint">
												<span>Shipping</span>
												<span>Quoted separately</span>
											</View>
										)}
										<View className="wsTotalRow total">
											<span>Total ({currency})</span>
											<span className="num">{money(currency, total)}</span>
										</View>
									</View>

									<View className="wsReceiver">
										<span className="mnKicker">Ship to</span>
										<View className="wsReceiverGrid">
											{field('givenName', 'Name', {
												required: true,
												autoComplete: 'given-name',
											})}
											{field('familyName', 'Family name', {
												autoComplete: 'family-name',
											})}
											{field('phoneCountryCode', 'Country code', {
												maxLength: 6,
												autoComplete: 'tel-country-code',
											})}
											{field('phoneNumber', 'Phone', {
												required: true,
												autoComplete: 'tel-national',
											})}
											{field('street1', 'Address', {
												required: true,
												autoComplete: 'address-line1',
											})}
											{field('street2', 'Address line 2', {
												autoComplete: 'address-line2',
											})}
											{field('city', 'City', { autoComplete: 'address-level2' })}
											{field('stateName', 'State / Province', {
												autoComplete: 'address-level1',
											})}
											{field('zip', 'Postal code', { autoComplete: 'postal-code' })}
											{field('countryName', 'Country', {
												required: true,
												autoComplete: 'country-name',
											})}
											{field('countryIsoCode', 'Country code (ISO 2)', {
												required: true,
												maxLength: 2,
												autoComplete: 'country',
											})}
										</View>
										<View className="mnField">
											<label className="mnLabel" htmlFor="ws-memo">
												Note
											</label>
											<input
												id="ws-memo"
												type="text"
												className="mnInput"
												value={memo}
												maxLength={255}
												onChange={e => setMemo(e.target.value)}
											/>
										</View>
									</View>

									{placeResult !== null && (
										<p className={placeResult.ok ? 'wsResult' : 'mnError'}>
											{placeResult.message}
										</p>
									)}

									<View className="mnAccountActions">
										<button
											type="button"
											className="mnCta"
											disabled={!canPlace}
											onClick={() => setIsConfirmOpen(true)}
										>
											{isPlacing ? 'Placing…' : 'Place order'}
										</button>
										<span className="mnMeta">
											Stock is reserved when the order is placed. An invoice follows by
											email.
										</span>
									</View>
								</View>
							</View>
						</FullModalLayout>
					)}
					<MonolithOrderList
						label="Wholesale orders"
						rows={orderList.map(anOrder => ({
							id: anOrder.id,
							date: wholesaleDate(anOrder.createdAt),
							status: wholesaleStatusTitle(anOrder),
							amount: wholesaleMoney(anOrder.currency, anOrder.paidAmount),
						}))}
						isLoading={orderListStatus === 'loading' || orderListStatus === 'init'}
						emptyLabel="No wholesale orders yet."
						onDetail={openDetail}
						onDownloadExcel={() => downloadWholesaleOrdersExcel(orderList)}
						actions={
							<button
								type="button"
								className="mnCta"
								disabled={!canOpenNew}
								onClick={() => setView('new')}
							>
								New order
							</button>
						}
						notice={
							<>
								{placeResult?.ok && <p className="wsResult">{placeResult.message}</p>}
								{newHint !== '' && <p className="mnMeta">{newHint}</p>}
							</>
						}
					/>

					{/* 자세히 — /customer/wholesale/:orderId. 맨 아래 Download PDF (MonolithOrderDetailModal) */}
					{detailOrder !== null && (
						<MonolithOrderDetailModal
							key={detailOrder.id}
							title="Wholesale order"
							onClose={closeDetail}
							onDownloadPdf={() => downloadWholesaleOrderDocument(detailOrder)}
						>
							<View css={style} className="wsDetailView">
								<WholesaleOrderBand order={detailOrder} />
							</View>
						</MonolithOrderDetailModal>
					)}
				</View>
			</View>

			<Footer />

			<MonolithConfirmModal
				isOpen={isConfirmOpen}
				title="Place order"
				body={`${lines.map(l => `${l.name} ×${l.qty}`).join(', ')} — ${money(
					currency,
					total,
				)}. Stock is reserved now. An invoice follows by email.`}
				confirmLabel="Place order"
				cancelLabel="Back"
				onConfirm={onPlace}
				onCancel={() => setIsConfirmOpen(false)}
			/>
		</View>
	)
}

/** 도매 주문 한 건 — 자세히 모달의 본문. 칸은 My Order 와 같다(customerOrderList.style) */
const WholesaleOrderBand = ({ order }: { order: OrderType }) => {
	const address = wholesaleAddress(order)
	const hasShipment = hasWholesaleShipment(order)

	return (
		<View className="mnOrder">
			<View className="mnOrderHead">
				<View className="mnOrderHeadLeft">
					<span className="mnMeta">Order No.</span>
					<p className="mnOrderNo">{order.id}</p>
				</View>
				<View className="mnOrderHeadRight">
					<span className="mnMeta mnOrderStatus">{wholesaleStatusTitle(order)}</span>
					<span className="mnMeta">
						{dayjs(order.createdAt).format('MMM DD YYYY · HH:mm')}
					</span>
				</View>
			</View>

			<View className="mnOrderDetail">
				<View className="mnOrderBlock">
					<span className="mnKicker">Items</span>
					<View className="mnOrderItemList">
						{order.detailList.map(anItem => (
							<View key={anItem.id || anItem.productId} className="mnOrderItem">
								<span className="mnOrderItemName">{anItem.productName}</span>
								<span className="mnOrderItemQty">
									{money(order.currency, anItem.unitAmount)} ×{anItem.quantity}
								</span>
							</View>
						))}
					</View>
				</View>

				<View className="mnOrderBlock">
					<span className="mnKicker">Payment</span>
					<View className="mnOrderTable">
						<View className="mnOrderTableRow">
							<span>Subtotal</span>
							<span>{money(order.currency, order.totalAmount)}</span>
						</View>
						{order.vatAmount > 0 && (
							<View className="mnOrderTableRow">
								<span>VAT</span>
								<span>{money(order.currency, order.vatAmount)}</span>
							</View>
						)}
						{order.shippingAmount > 0 && (
							<View className="mnOrderTableRow">
								<span>Shipping</span>
								<span>{money(order.currency, order.shippingAmount)}</span>
							</View>
						)}
						{order.dutyAmount > 0 && (
							<View className="mnOrderTableRow">
								<span>Duties</span>
								<span>{money(order.currency, order.dutyAmount)}</span>
							</View>
						)}
						<View className="mnOrderTableRow total">
							<span>{`Total (${order.currency})`}</span>
							<span>{money(order.currency, order.paidAmount)}</span>
						</View>
						<View className="mnOrderTableRow">
							<span>Paid</span>
							<span>
								{order.paidYn === 1 && order.paidAt
									? dayjs(order.paidAt).format('MMM DD YYYY')
									: 'Not yet'}
							</span>
						</View>
					</View>
				</View>

				<View className="mnOrderBlock">
					<span className="mnKicker">Recipient</span>
					<View className="mnOrderTable">
						<View className="mnOrderTableRow">
							<span>Name</span>
							<span>
								{order.receiverGivenName} {order.receiverFamilyName}
							</span>
						</View>
						<View className="mnOrderTableRow">
							<span>Address</span>
							<span>{address}</span>
						</View>
					</View>
				</View>

				<View className="mnOrderBlock">
					<span className="mnKicker">Shipping</span>
					<View className="mnOrderTable">
						<View className="mnOrderTableRow">
							<span>Carrier</span>
							<span>{hasShipment ? order.deliveryCompany : '—'}</span>
						</View>
						<View className="mnOrderTableRow">
							<span>Tracking No.</span>
							<span>{hasShipment ? order.trackingNumber : '—'}</span>
						</View>
						{order.deliveryCompletedAt && (
							<View className="mnOrderTableRow">
								<span>Delivered</span>
								<span>{dayjs(order.deliveryCompletedAt).format('MMM DD YYYY')}</span>
							</View>
						)}
					</View>
				</View>
			</View>
		</View>
	)
}

export default MonolithCustomerWholesalePage

const style = css({
	...customerAccountStyle,
	...customerOrderListStyle,

	// 2026-10-06 계정 공용 가운데 760 칼럼을 그대로 쓴다(사용자 지시 — 전폭 예외 폐지). 표는 칼럼 안에서 가로 스크롤
	'.wsBody': { gap: 56 },

	'.wsOrderBand': {
		display: 'flex',
		flexDirection: 'column',
		gap: 32,
		maxWidth: 860,
	},
	'.wsBandHead': { display: 'flex', flexDirection: 'column', gap: 10 },

	'.wsTable': {
		borderTop: `1px solid ${mono.line}`,
		'.wsRow': {
			display: 'grid',
			gridTemplateColumns: 'minmax(0, 1fr) 140px 110px 150px',
			gap: 16,
			alignItems: 'center',
			padding: '14px 0',
			borderBottom: `1px solid ${mono.line}`,
			fontSize: 16,
			fontWeight: 300,
			color: mono.ink,
			'@media (max-width: 600px)': {
				gridTemplateColumns: 'minmax(0, 1fr) 90px 70px',
				'& > :nth-of-type(4)': { display: 'none' },
			},
		},
		'.wsRowHead': {
			fontSize: 13,
			letterSpacing: '.14em',
			textTransform: 'uppercase',
			fontWeight: 500,
			color: mono.faint,
			padding: '10px 0',
		},
		'.wsProduct': { display: 'flex', flexDirection: 'column', gap: 4 },
		'.wsProductName': { fontWeight: 400 },
		'.wsQty': { width: 80, textAlign: 'right', padding: '6px 0' },
		'.right': { textAlign: 'right' },
		'.num': { fontVariantNumeric: 'tabular-nums' },
	},

	'.wsTotals': {
		maxWidth: 420,
		marginLeft: 'auto',
		width: '100%',
		'.wsTotalRow': {
			display: 'flex',
			justifyContent: 'space-between',
			alignItems: 'center',
			gap: 16,
			padding: '10px 0',
			borderBottom: `1px solid ${mono.line}`,
			fontSize: 15,
			fontWeight: 300,
			color: mono.ink,
			'&.faint': { color: mono.faint },
			'&.total': { fontWeight: 500, borderBottom: `1px solid ${mono.ink}` },
			'.num': { fontVariantNumeric: 'tabular-nums' },
		},
		'.wsAmountInput': { width: 140, textAlign: 'right', padding: '6px 0' },
	},

	'.wsReceiver': {
		display: 'flex',
		flexDirection: 'column',
		gap: 24,
		'.wsReceiverGrid': {
			display: 'grid',
			gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
			gap: '24px 32px',
			'@media (max-width: 600px)': { gridTemplateColumns: '1fr' },
		},
	},

	'.wsResult': { fontSize: 15, fontWeight: 400, color: mono.ink, margin: 0 },

	'.mnCta:disabled': { background: mono.lineSoft, color: '#FFFFFF', cursor: 'default' },

	'.wsListBand': { display: 'flex', flexDirection: 'column', gap: 20 },
	'.wsNewView': {
		display: 'flex',
		flexDirection: 'column',
		gap: 28,
		padding: '8px 0 40px',
	},

	// 자세히 모달 — 밴드의 위 선·패딩은 모달 제목 아래에서는 필요 없다
	'.wsDetailView .mnOrder': {
		borderTop: 0,
		paddingTop: 8,
	},
})
